import React from "react";
import styled from "styled-components";
// CONSTANTS
import { COLORS } from "Styles/Constants";

/**
 * Branded error screen shown if a game's JS chunk (or an asset it needs at
 * import time) fails to download — e.g. a dropped connection or a stale
 * cached chunk after a deploy. Instead of an unhandled error bubbling to the
 * app root (which would leave a blank/white screen), the user sees a clear,
 * on-brand message with a Retry action.
 *
 * Retry simply clears the error state so React re-renders the lazy child;
 * @loadable/component then re-attempts the dynamic import. This recovers
 * WITHOUT a full page reload in the common "temporary network blip" case.
 */
const ErrorWrapper = styled.div`
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	width: 100%;
	height: 100%;
	min-height: 100vh;
	z-index: 9999;
	position: fixed;
	display: flex;
	padding: 24px;
	text-align: center;
	align-items: center;
	flex-direction: column;
	justify-content: center;
	color: ${COLORS.WHITE};
	background-color: ${COLORS.BLUE_BACKGROUND};

	.error-title {
		margin: 0 0 10px;
		font-size: 22px;
		letter-spacing: 1px;
		text-transform: uppercase;
	}

	.error-subtitle {
		margin: 0 0 26px;
		font-size: 15px;
		max-width: 420px;
		opacity: 0.85;
		line-height: 1.5;
	}

	.retry-btn {
		border: none;
		cursor: pointer;
		color: ${COLORS.WHITE};
		font-size: 15px;
		padding: 12px 34px;
		border-radius: 30px;
		letter-spacing: 1px;
		text-transform: uppercase;
		background-color: ${COLORS.SKY_BLUE};
		transition: background-color 0.25s ease;

		&:hover {
			background-color: ${COLORS.SKY_BLUE_DARK};
		}
	}
`;

class GameErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError() {
		return { hasError: true };
	}

	componentDidCatch(error, info) {
		// eslint-disable-next-line no-console
		console.error("Science game failed to load:", error, info);
	}

	handleRetry = () => this.setState({ hasError: false });

	render() {
		if (this.state.hasError) {
			return (
				<ErrorWrapper>
					<p className="error-title">We couldn't load the game</p>
					<p className="error-subtitle">Something interrupted loading. Please check your connection and try again.</p>
					<button className="retry-btn" onClick={this.handleRetry}>
						Retry
					</button>
				</ErrorWrapper>
			);
		}

		return this.props.children;
	}
}

export default GameErrorBoundary;
